// Rich Text Content Format (Quill Delta vs HTML) utilities

/**
 * Prepares content for API submission
 * If contentJson (Quill Delta) exists, stringify it; otherwise use htmlContent
 */
export const prepareContentForAPI = (contentJson: any, htmlContent: string): string => {
  return contentJson ? JSON.stringify(contentJson) : htmlContent;
};

/**
 * Parses content from API for editing
 * If content looks like JSON, parse it and convert to HTML; otherwise use as HTML
 */
export const parseContentForEditing = (content: string) => {
  let parsedContent = content;
  let jsonContent = null;

  try {
    if (content && (content.startsWith('{') || content.startsWith('['))) {
      jsonContent = JSON.parse(content);
      parsedContent = convertQuillJsonToHtml(jsonContent);
    }
  } catch {
    parsedContent = content; // If JSON parsing fails, treat as HTML
  }

  return { parsedContent, jsonContent };
};

/**
 * Converts Quill Delta JSON to HTML
 * This is a simplified converter - you can enhance it based on your needs
 */
export const convertQuillJsonToHtml = (delta: any): string => {
  if (!delta || !delta.ops) return '';

  let html = '';
  let i = 0;
  const usedOps = new Set<number>();
  
  while (i < delta.ops.length) {
    const op = delta.ops[i];
    
    if (op.insert && !usedOps.has(i)) {
      let text = op.insert;
      
      // First check if the NEXT operation has header attribute
      if (i + 1 < delta.ops.length) {
        const nextOp = delta.ops[i + 1];
        if (nextOp.attributes && nextOp.attributes.header) {
          const headerLevel = nextOp.attributes.header;
          
          // Use current text as header
          const headerText = text.replace(/\n/g, '').trim();
          if (headerText) {
            html += `<h${headerLevel}>${headerText}</h${headerLevel}>`;
          }
          
          // Mark both operations as used
          usedOps.add(i);
          usedOps.add(i + 1);
          
          i += 2; // Skip both operations
          continue;
        }
      }
      
      // Check if this operation has header attribute
      if (op.attributes && op.attributes.header) {
         const headerLevel = op.attributes.header;
         
         // Look ahead to find the actual text content for the header
         let headerText = '';
         let j = i + 1;
         
         // Collect text from subsequent operations until we hit another operation with attributes
         while (j < delta.ops.length) {
           const nextOp = delta.ops[j];
           if (nextOp.insert && !nextOp.attributes) {
             headerText += nextOp.insert;
             j++;
           } else {
             break;
           }
         }
         
         // If no text found after header, look back to previous text
         if (!headerText.trim()) {
           // Look back to find the previous text operation
           let k = i - 1;
           while (k >= 0) {
             const prevOp = delta.ops[k];
             if (prevOp.insert && !prevOp.attributes && !usedOps.has(k)) {
               headerText = prevOp.insert.replace(/\n/g, '').trim();
               usedOps.add(k); // Mark this operation as used
               break;
             }
             k--;
           }
         } else {
           // Clean up the header text
           headerText = headerText.replace(/\n/g, '').trim();
           // Mark the operations we collected as used
           for (let m = i + 1; m < j; m++) {
             usedOps.add(m);
           }
         }
         
         // Create the header
         if (headerText) {
           html += `<h${headerLevel}>${headerText}</h${headerLevel}>`;
         }
         
         // Mark current operation as used
         usedOps.add(i);
         
         // Skip the operations we processed
         i = j;
         continue;
       }
      
      // Handle line breaks
      if (typeof text === 'string' && text.includes('\n')) {
        text = text.replace(/\n/g, '<br>');
      }
      
      // Apply other formatting
      if (op.attributes) {
        const attrs = op.attributes;
        let openTags = '';
        let closeTags = '';
        
        if (attrs.bold) {
          openTags += '<strong>';
          closeTags = '</strong>' + closeTags;
        }
        if (attrs.italic) {
          openTags += '<em>';
          closeTags = '</em>' + closeTags;
        }
        if (attrs.underline) {
          openTags += '<u>';
          closeTags = '</u>' + closeTags;
        }
        if (attrs.strike) {
          openTags += '<s>';
          closeTags = '</s>' + closeTags;
        }
        if (attrs.list) {
          if (attrs.list === 'ordered') {
            openTags += '<ol><li>';
            closeTags = '</li></ol>' + closeTags;
          } else {
            openTags += '<ul><li>';
            closeTags = '</li></ul>' + closeTags;
          }
        }
        
        text = openTags + text + closeTags;
      }
      
      html += text;
    }
    
    i++;
  }
  
  return html;
};

/**
 * Converts HTML to Quill Delta format (simplified)
 * This is a basic converter - you might want to use a more robust solution
 */
export const convertHtmlToQuillDelta = (html: string): any => {
  // This is a simplified implementation
  // For production, consider using a proper HTML to Delta converter
  const ops: any[] = [];
  
  if (html) {
    // Split by line breaks and create insert operations
    const lines = html.split('<br>');
    lines.forEach((line, index) => {
      if (line.trim()) {
        ops.push({ insert: line.replace(/<[^>]*>/g, '') }); // Strip HTML tags for now
      }
      if (index < lines.length - 1) {
        ops.push({ insert: '\n' });
      }
    });
  }
  
  return { ops };
};
